import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  AddCircleOutline,
  ContentCopyOutlined,
  RemoveRedEyeOutlined,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Search } from "lucide-react";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { DEFAULT_LIMIT, DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../utils/constants";
import { EditIcon } from "../comps/CustomIcons";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import { useGetApi } from "../hooks/useGetApi";
import { 
  getSharingTemplatesApi, 
  deleteSharingTemplateApi,
  updateTemplateStatusApi,
  addSharingTemplateApi
} from "../services/sharingTemplate.service";

export default function MasterSharing() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchBy] = useState("templateName");
  const [sortModel, setSortModel] = useState([{ field: "templateName", sort: "asc" }]);
  const searchByLabel = "Template Name";

  // Fetch templates
  const { 
    dataList: templates = [], 
    dataCount: totalTemplates = 0, 
    isLoading, 
    isError, 
    refetch 
  } = useGetApi({
    apiFunction: getSharingTemplatesApi,
    params: {
      search: debouncedSearch,
      page: page + 1,
      limit: rowsPerPage
    }
  });

  // Add event listener for template updates
  useEffect(() => {
    const handleTemplateUpdate = (event) => {
      // Get the updated template ID from the event
      const { id } = event.detail;
      
      // Refetch the data - useGetApi will handle the state update
      refetch();
    };
    
    window.addEventListener('templateUpdated', handleTemplateUpdate);
    return () => {
      window.removeEventListener('templateUpdated', handleTemplateUpdate);
    };
  }, [refetch]);

  // Clear search and reset table
  const handleClearSearch = () => {
    setSearch('');
    setDebouncedSearch('');
    setPage(0);
    // Force a refetch to ensure we get fresh data
    refetch();
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNewTemplate = () => {
    navigate("add");
  };

  const handleView = (template) => {
    console.log('View template:', template);
    // Create a view route or open in edit mode for read-only viewing
    navigate(`/master/sharing/edit/${template._id}?mode=view`);
  };

  const handleEdit = (template) => {
    navigate(`/master/sharing/edit/${template._id}`);
  };

  const handleCopy = async (template) => {
    try {
      const templateCopy = { ...template };
      delete templateCopy._id;
      delete templateCopy.createdAt;
      delete templateCopy.updatedAt;
      templateCopy.templateName = `${template.templateName} (Copy)`;
      templateCopy.active = false; // New copy should be inactive by default
      
      // Call the API to create a copy
      const result = await addSharingTemplateApi(templateCopy);
      
      if (result) {
        toast.success('Template copied successfully');
        refetch();
      } else {
        toast.error('Failed to copy template');
      }
    } catch (error) {
      console.error('Error copying template:', error);
      toast.error('Failed to copy template');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteSharingTemplateApi(id);
        toast.success('Template deleted successfully');
        refetch();
      } catch (error) {
        console.error('Error deleting template:', error);
        toast.error('Failed to delete template');
      }
    }
  };

  const handleStatusChange = async (template) => {
    try {
      const newStatus = template.isActive === false ? true : false;
      await updateTemplateStatusApi(template._id, newStatus);
      toast.success(`Template ${newStatus ? 'activated' : 'deactivated'} successfully`);
      refetch();
    } catch (error) {
      console.error('Error updating template status:', error);
      toast.error('Failed to update template status');
    }
  };

  const handleClose = () => navigate(-1);

  const rawRows = (templates || []).map((row) => ({
    id: row._id,
    _id: row._id,
    templateName: row?.templateName || "-",
    status: row?.active === true ? "Active" : "Inactive",
    _original: row,
  }));

  const searchLower = String(search || "").toLowerCase();
  const filteredRows = searchLower ? rawRows.filter((r) => String(r.templateName ?? "").toLowerCase().includes(searchLower)) : rawRows;

  const sortField = sortModel[0]?.field || "templateName";
  const sortOrder = sortModel[0]?.sort || "asc";
  const sortedRows = [...filteredRows].sort((a, b) => { const cmp = String(a[sortField] ?? "").localeCompare(String(b[sortField] ?? ""), undefined, { numeric: true }); return sortOrder === "asc" ? cmp : -cmp; });
  const totalFiltered = sortedRows.length;
  const rows = totalFiltered > rowsPerPage ? sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : sortedRows;

  const getColumns = useCallback(() => [
    { field: "templateName", headerName: "Template Name", flex: 1, minWidth: 180, headerAlign: "left", align: "left", sortable: true },
    { field: "status", headerName: "Status", flex: 0.6, minWidth: 90, headerAlign: "center", align: "center", sortable: true, renderCell: (params) => <Chip label={params.value || "Active"} color={params.value === "Inactive" ? "error" : "success"} size="small" variant="outlined" /> },
    { field: "actions", headerName: "Actions", flex: 0, width: 140, minWidth: 120, headerAlign: "right", align: "right", sortable: false, renderCell: (params) => (
      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
        <IconButton size="small" onClick={() => handleEdit(params.row._original)}><EditIcon sx={{ color: "transparent", fontSize: "18px", cursor: "pointer" }} /></IconButton>
        <IconButton size="small" onClick={() => handleCopy(params.row._original)}><ContentCopyOutlined sx={{ fontSize: "18px", cursor: "pointer" }} /></IconButton>
        <IconButton size="small" onClick={() => handleView(params.row._original)}><RemoveRedEyeOutlined sx={{ cursor: "pointer", fontSize: "18px" }} /></IconButton>
      </Box>
    ) },
  ], []);

  const dataGridSx = {
    border: "none", "& .MuiDataGrid-root": { border: "none", width: "100%" }, "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0", padding: isMobile ? "4px" : "8px", fontSize: isMobile ? "12px" : "14px" },
    "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" }, "& .MuiDataGrid-columnHeader": { backgroundColor: "#f5f5f5", fontWeight: "bold" },
    "& .MuiDataGrid-row": { "&:hover": { backgroundColor: "#f5f5f5" }, "&:nth-of-type(odd)": { backgroundColor: "#fafafa" }, "&:nth-of-type(even)": { backgroundColor: "white" } },
    "& .MuiDataGrid-footerContainer": { borderTop: "2px solid #e0e0e0", backgroundColor: "#f9f9f9" },
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>

        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              padding: 2,
              marginTop: 2,
              borderRadius: 2,
              marginRight: "90px",
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 0,
                mb: "20px",
                color: "#5D6A82",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Open Sans",
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "29px",
                  letterSpacing: "0px",
                  color: "inherit",
                }}
              >
                Sharing Master
              </Typography>
              <Button
                variant="contained"
                sx={{
                  textTransform: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
                onClick={handleNewTemplate}
              >
                <AddCircleOutline />
                <Typography sx={{ color: "white" }}>New Template</Typography>
              </Button>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: "20px",
              }}
            >
              <TextField placeholder={`Search by ${searchByLabel}`} size="small" sx={{ width: "300px" }} slotProps={{ input: { startAdornment: (
                <InputAdornment position="start">
                  <select className="uid-select" value={searchBy} style={{ border: "none", outline: "none", background: "transparent", cursor: "pointer", color: "#333", fontWeight: "500" }} readOnly>
                    <option value="templateName">Template Name</option>
                  </select>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                  <Search />
                </InputAdornment>
              ), endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={handleClearSearch} edge="end"><CloseIcon fontSize="small" /></IconButton></InputAdornment> : undefined } }} value={search || ""} onChange={(e) => setSearch(e.target.value || "")} />
            </Box>
            {isLoading ? <Loader /> : isError ? <ErrorMessage /> : (
              <Box sx={{ height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 320px)", md: "calc(100vh - 350px)" }, width: "100%", minHeight: "300px", overflow: "hidden" }}>
                <DataGrid rows={rows} columns={getColumns()} paginationModel={{ page, pageSize: rowsPerPage }} onPaginationModelChange={(m) => { setPage(m.page); setRowsPerPage(m.pageSize); }} pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS} rowCount={totalFiltered} sortModel={sortModel} onSortModelChange={(m) => setSortModel(m)} disableRowSelectionOnClick loading={isLoading} sx={dataGridSx} components={{ NoRowsOverlay: () => <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}><Typography variant="body2" color="text.secondary">No templates found</Typography></Box> }} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
